// StorefrontShell — persistent chrome that wraps every marketplace route.
//
// Extracted from the catalog-only toolbar that MarketplaceEmbed.tsx retained
// in Task 2; Task 6 lifted it into a shell wrapping every route (fixes spec
// defect D2 — the toolbar disappeared once you navigated away from catalog).
//
// Task 11 (marketplace mini-site redesign, spec §7.1-7.3 / D1/D8/D10):
//   - D10 hero: a photographic band driven by the operator's StoreContent
//     hero fields (LIGHT art direction since operator item (k2), 2026-08-16 —
//     see the HERO_* block below). Fail-open like the old hero band: no
//     hero_headline configured → no hero at all.
//     Operator directive 2026-08-16: the band is now ONE CENTRED COLUMN over
//     the backdrop. The staggered product montage and the single
//     uploaded-image card are both GONE ("too busy"), as is the earlier
//     "From $X" min-price chip; an operator UPLOAD now replaces the curated
//     photo as the backdrop itself (with a contrast scrim, and only after a
//     preload proves it loadable — see StorefrontHero).
//   - Toolbar: debounced search input (SEARCH_DEBOUNCE_MS retained) + a sort
//     dropdown whose option values are exactly the backend's sort allowlist.
//   - Active-filter chips row: one ✕ chip per active category / type / search
//     filter, clearing that filter via the router's callbacks.
//   - RETIRED: the Task-12 category <select> and its GET /categories fetch —
//     the catalog facets (surfaced by CatalogView's rail) replace both.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useAuth } from "../../embed-shared/auth-context";
import { useCart } from "../../embed-shared/cart-context";
import { useEmbedTokens, EmbedInput, EmbedSelect } from "../../embed-shared/primitives";
import { LIGHT_COLORS } from "../../embed-shared/tokens";
import { useStorefrontConfig } from "./storefront-config";
import { StoreFooterBand } from "./StoreBands";
import { typeLabel } from "./ProductCard";
import type { CatalogSort } from "./CatalogView";
// Type-only (erased at build): the shell takes the router's OWN route
// discriminant so "which view am I on" has exactly one definition. A value
// import here would be a module cycle (MarketplaceEmbed imports this file);
// `import type` emits nothing, so there is no cycle at runtime.
import type { Route } from "./MarketplaceEmbed";
import type { CatalogFacets } from "../../embed-shared/types";

// Debounce interval between the last keystroke and reporting the search
// value up to the parent (pinned by the spec — see task-12 brief Step 3).
const SEARCH_DEBOUNCE_MS = 300;

// Layout width for the search + sort control cluster (TD-313). These are
// LAYOUT tuning values (how the toolbar wraps), not spacing-scale values, so
// they do not map to t.spacing tokens — but they are named here rather than
// left as bare inline literals so the wrapping behaviour has one home and a
// rationale, satisfying the no-hardcoded-magic-numbers-in-place intent.
const CONTROLS_FLEX_BASIS = 240; // search+sort row preferred width
const CONTROLS_MAX_WIDTH = 480; // cap so controls never dominate the toolbar
const SORT_MIN_WIDTH = 150; // keep the sort <select> usable when wrapped

// Operator item (n), 2026-08-16: "make the oval a little wider". Added to the
// shared buttonHeight to give the cart pill's width — at width == height it
// was a circle, and radius 999 was the only thing suggesting an oval. Height
// is deliberately NOT derived from this: it stays exactly t.buttonHeight so
// the pill keeps matching the search input and sort select in the same
// toolbar row (the Task 4a contract, pinned by a co-located test).
//
// A control-geometry tuning value, so — like CONTROLS_FLEX_BASIS and
// SORT_MIN_WIDTH above — it is named here rather than mapped onto a t.spacing
// token: the spacing scale describes gaps BETWEEN things, and the nearest
// steps (sm=8, md=16) would give a still-circular 52px or an over-stretched
// 60px. 12px lands at 56×44 — unmistakably an oval, ~27% wider, still compact
// enough for the icon+badge to stay optically centred.
//
// Exported so the co-located test derives the expected width from this
// constant plus the token, never from a hardcoded "56px" (§55).
export const CART_PILL_EXTRA_WIDTH = 12;

// Sort options for the toolbar dropdown. The VALUE strings are the backend
// catalog allowlist (invalid ⇒ 400 — CatalogView.CatalogSort pins the union);
// the labels are display-only (mock wording).
const SORT_OPTIONS: Array<{ value: CatalogSort; label: string }> = [
  { value: "featured", label: "Sort: Featured" },
  { value: "name", label: "Name A–Z" },
  { value: "price", label: "Price: low to high" },
  { value: "-price", label: "Price: high to low" },
];

// D10 hero visual constants (mock `section.hero`). These are HERO-SPECIFIC
// art direction — the band is deliberately LIGHT in BOTH themes (see
// HERO_INK) — so they are intentionally NOT theme tokens; every value that IS
// theme-sensitive (chip borders, radii, spacing) flows through
// useEmbedTokens(). Named here rather than inline so the band's art direction
// has one home.
//
// Task 5 (spec H4) brought the gradient backdrop, the bigger min-height/type,
// and a single reduced-motion-aware entrance animation. Its "floating cards"
// half — the staggered montage tiles and their per-tile geometry/elevation/
// float constants — was REMOVED by the 2026-08-16 operator directive, along
// with Task 6's single uploaded-image card. What survives here is the
// backdrop, the ink, the type, and the copy rhythm.
//
// Operator item (k2), 2026-08-16: LIGHT MODE. The photo is no longer shaded,
// the copy is black, and all four edges melt into white. Everything below is
// tuned for a near-white composition — do NOT reinstate dark-scene values.
//
// Bottom-most layer of the hero background stack: what shows while the photo
// is still loading and what the band looks like if the photo ever 404s. It
// mirrors the photo's own palette (near-white overall, cool blue lower-left,
// warm amber lower-right) so the fallback and the photo are interchangeable
// under black copy. Was a navy gradient until (k2); a dark fallback under
// black text would have been unreadable for the whole load window.
const HERO_BACKGROUND =
  "radial-gradient(60% 90% at 50% -12%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 62%), " +
  "radial-gradient(70% 120% at 8% 96%, rgba(90,159,216,0.24) 0%, rgba(90,159,216,0) 62%), " +
  "radial-gradient(70% 120% at 92% 96%, rgba(240,134,74,0.20) 0%, rgba(240,134,74,0) 60%), " +
  "linear-gradient(115deg, #F5F8FE 0%, #EAF1FB 48%, #FDF2EA 100%)";
// (k2) Hero copy ink. Pinned to the LIGHT palette's text colour rather than
// t.colors.text: the hero art is light in BOTH themes, so the theme-swapped
// token would render near-white ink (#F4F4F5) on a near-white photo in dark
// mode. Derived from the token, not duplicated (§55).
const HERO_INK = LIGHT_COLORS.text;
// (k2) Subheadline — subordinate to the headline but still legible over the
// photo's most saturated regions. Measured against the two worst-case pixels
// in the current artwork (#5A9FD8 blue ≈ 4.8:1, #F0864A amber ≈ 5.1:1), both
// clearing WCAG AA for normal text. A tint of HERO_INK rather than neutral
// black measured BELOW AA over the blue, so this stays a black alpha.
const HERO_TEXT_SOFT = "rgba(0,0,0,0.70)";
// Operator item (k), 2026-08-16: the DEFAULT hero background is the aldelo.com
// /marketplace page-hero photo, copied to OUR embed CDN (stable key — Framer's
// content-hashed URLs change on every re-upload). (k2) removed the navy scrim
// that used to sit above it: the photo now renders unshaded, and the operator
// explicitly overrode H6 ("text never rides raw photography") for this band.
const HERO_BG_IMAGE_URL = "https://storefront-js.aldelo.ai/marketplace-hero-bg.jpg";
// (k2) All four edges blend white into the photo, so the band melts into the
// host page instead of ending in a hard rectangle. Four explicit gradient
// layers (stacked ABOVE the photo) rather than the inset box-shadow vignette
// this replaced — a vignette concentrates its weight in the corners, whereas
// one gradient per edge gives an even melt along each full edge.
//
// The two stops differ because the band is wide and short: an equal
// percentage would eat far more pixels horizontally than vertically. At the
// 440px desktop min-height, 30% ≈ 132px top and bottom; on a ~1200px-wide
// host page, 15% ≈ 180px left and right — visually comparable bands, and the
// centre of the composition stays clear.
const HERO_EDGE_BLEND_VERTICAL_STOP = "30%";
const HERO_EDGE_BLEND_HORIZONTAL_STOP = "15%";
const HERO_EDGE_BLEND =
  `linear-gradient(to bottom, #fff 0%, rgba(255,255,255,0) ${HERO_EDGE_BLEND_VERTICAL_STOP}), ` +
  `linear-gradient(to top, #fff 0%, rgba(255,255,255,0) ${HERO_EDGE_BLEND_VERTICAL_STOP}), ` +
  `linear-gradient(to right, #fff 0%, rgba(255,255,255,0) ${HERO_EDGE_BLEND_HORIZONTAL_STOP}), ` +
  `linear-gradient(to left, #fff 0%, rgba(255,255,255,0) ${HERO_EDGE_BLEND_HORIZONTAL_STOP})`;
/**
 * Contrast guard for an operator-UPLOADED backdrop. Inserted directly above
 * the upload and NEVER on the default path — (k2) deliberately chose "no
 * scrim" for the curated photo, which is a known-bright pastel composition,
 * and that decision stands.
 *
 * An upload is the opposite case: an arbitrary image nobody has vetted, which
 * may be solid black. The copy rides the artwork raw in near-black HERO_INK
 * (the operator's explicit H6 override), so an unguarded dark upload would put
 * black text on black and fail WCAG 1.4.3 outright.
 *
 * The alpha is chosen against that WORST case — a solid #000 upload, where the
 * composited backdrop is a flat grey of exactly this alpha. The edge blends
 * are fully transparent through the middle of the band, so they contribute
 * nothing where the copy actually sits and cannot be counted on here:
 *
 *   backdrop      rgb(158,158,158)              relative luminance 0.3424
 *   headline      HERO_INK #111827              luminance 0.0092  ->  6.63:1
 *   subheadline   HERO_TEXT_SOFT, i.e. 70% black composited over the backdrop
 *                 = rgb(47,47,47)               luminance 0.0289  ->  4.97:1
 *
 * AA needs 3:1 for the headline (large text at both 50px and 32px, and at
 * weight 700 by the bold bar too — see HERO_HEADLINE_WEIGHT's note) and 4.5:1
 * for the 20px subheadline (still under the 24px large-text bar, so the
 * stricter threshold applies even after the 16px → 20px continuity bump),
 * so the subheadline is the binding constraint and 0.62 clears it with ~10%
 * headroom while still letting a mid-tone photograph read through as a wash.
 * Below ~0.58 the subheadline drops under AA on a dark upload: this is an
 * accessibility control, not a taste knob.
 */
const HERO_UPLOAD_SCRIM_ALPHA = 0.62;
const HERO_UPLOAD_SCRIM =
  `linear-gradient(rgba(255,255,255,${HERO_UPLOAD_SCRIM_ALPHA}), ` +
  `rgba(255,255,255,${HERO_UPLOAD_SCRIM_ALPHA}))`;
/** Hero drops to a single column below this viewport width (mock @900). */
const HERO_DESKTOP_MIN_WIDTH = 768;
/** H4: hero min-height at desktop (was 300px). */
const HERO_MIN_HEIGHT_DESKTOP = 440;
/**
 * Max measure of the CENTRED copy column (2026-08-16 redesign — the hero is
 * one centred column, no longer copy-beside-montage).
 *
 * A px cap rather than a `ch` one, deliberately: `ch` resolves against the
 * element's OWN font-size, and this wrapper inherits the body's ~16px while
 * the thing it has to constrain is a 50px headline — a `ch` measure sized for
 * the caption would wrap the headline after two or three words. 720px holds
 * the desktop headline to roughly two lines on a wide host page while keeping
 * the copy from spanning the full band edge-to-edge.
 */
const HERO_COPY_MAX_WIDTH = 720;
/**
 * Caption measure, kept verbatim from item (l). Nested INSIDE
 * HERO_COPY_MAX_WIDTH and centred with auto side margins, so the paragraph
 * stays a comfortable ~46 characters even though the column around it is
 * wider for the headline's sake.
 *
 * Being a `ch` measure is what let the 2026-08-16 aldelo.com type-continuity
 * pass raise HERO_SUBHEADLINE_SIZE from 16px to 20px without touching this
 * line: `ch` resolves against the paragraph's OWN font-size, so the measure
 * stays 46 characters and simply gets physically wider (~370px → ~500px, still
 * comfortably inside the 720px column). A px cap would have silently tightened
 * the line count instead.
 */
const HERO_SUBHEADLINE_MAX_WIDTH = "46ch";
/**
 * ALDELO.COM TYPE CONTINUITY, 2026-08-16. Operator directive: "fix the
 * marketplace hero title and caption font style and font size, match it to the
 * other aldelo.com pages' hero's font size and style so there is a continuity
 * feeling."
 *
 * The values in this block are MEASURED, not chosen — computed styles read off
 * the live Framer pages at 1440px and 390px viewports. Three centred
 * page-heroes agree EXACTLY:
 *
 *   /restaurant-pos, /pricing, /why-choose-aldelo
 *     h1  desktop   50px / 700 / line-height 1.5 / letter-spacing normal
 *     h1  mobile    32px / 700 / line-height 1.5 / letter-spacing normal
 *   body copy (dominant paragraph style, 17 instances on /restaurant-pos)
 *     p             20px / 400 / line-height 1.5
 *
 * The TYPEFACE was never the discontinuity — embed-shared/tokens.ts already
 * ships Framer's own `Inter, "Inter Placeholder", system-ui, sans-serif`. What
 * read as "a different site" was LEADING and TRACKING: Framer's ramp applies
 * 1.5 leading and `normal` tracking at every size, display included, whereas
 * this band had been hand-tuned as display type (1.1 / -0.01em). On a two-line
 * headline that is a 105px vs 150px block — the loudest tell of the set, ahead
 * of the 48-vs-50 size gap that prompted the report.
 *
 * RE-MEASURE before changing any of these. The Framer site is edited outside
 * this repo, so these numbers can drift underneath us with no signal here.
 */
/** Headline size — the measured aldelo.com page-hero h1, both breakpoints. */
const HERO_HEADLINE_SIZE_DESKTOP = 50;
const HERO_HEADLINE_SIZE_MOBILE = 32;
/**
 * Headline leading. Was 1.1 (display-tuned, set with this band in isolation).
 *
 * This single value does most of the continuity work — and is the one most
 * likely to look "wrong" reviewed on its own, because 1.5 leading on a 50px
 * headline is loose by display-typography convention. That is deliberate and
 * is the whole point: the goal is matching the host site's type system, not
 * independently optimal headline setting. Judge it beside a Framer page, never
 * against the band alone.
 */
const HERO_HEADLINE_LINE_HEIGHT = 1.5;
/**
 * Caption size. Was 16px (H4, itself up from 14.5); 20px is the dominant body
 * style on aldelo.com. See HERO_SUBHEADLINE_MAX_WIDTH for why the `ch` measure
 * absorbs this change without altering the paragraph's character count.
 */
const HERO_SUBHEADLINE_SIZE = 20;
/**
 * Caption leading. Item (l), 2026-08-16, raised this to 1.55 so the paragraph
 * stopped reading as one dense block under the headline. 1.5 PRESERVES that
 * intent — still well clear of the ~1.2 browser default that prompted the fix —
 * while landing exactly on the aldelo.com body ramp. Item (l) is honoured, not
 * reverted.
 */
const HERO_SUBHEADLINE_LINE_HEIGHT = 1.5;
/**
 * Headline weight — 700, the measured aldelo.com page-hero h1.
 *
 * ⚠️ THIS DELIBERATELY REVERSES OPERATOR ITEM (o), 2026-08-16 ("the title in
 * hero for marketplace does not need to be bold"), which had moved this 800 →
 * 500. The conflict was surfaced to the operator BY NAME alongside the
 * measurement, and the reversal was explicitly approved the same day: full
 * continuity with the host site won over the standalone not-bold preference.
 * The co-located test that pinned `< 700` was rewritten to pin 700 for the
 * same reason — a directive change, NOT a test bent to fit code (§13). Do not
 * "restore" 500 on the strength of item (o) alone; item (o) is superseded.
 *
 * ACCESSIBILITY — the weight change is contrast-NEUTRAL, verified rather than
 * assumed. Contrast ratio is a colour-pair computation: HERO_INK (#111827)
 * against the artwork's two worst-case pixels stays 6.24:1 (#5A9FD8 blue) and
 * 6.94:1 (#F0864A amber) at ANY weight. The WCAG "large text" allowance not
 * only survives, it now qualifies TWO independent ways instead of one: large
 * text is >=24px regular OR >=18.66px bold, and at 700 both sizes (50px
 * desktop / 32px mobile) clear BOTH bars. So the applicable threshold stays
 * 3:1 and we clear it 2x; even held to the stricter 4.5:1 normal-text bar the
 * headline still passes. Do not treat this constant as an a11y control.
 */
const HERO_HEADLINE_WEIGHT = 700;
/**
 * Headline tracking — `normal`, the measured aldelo.com page-hero h1.
 *
 * The history: -0.02em was tuned for the old 800 weight, where the
 * letterforms' own mass crowds the counters and negative tracking is
 * corrective; item (o) then relaxed it to -0.01em for 500. Both were optical
 * tuning of a headline considered in isolation, and both were reasonable on
 * those terms. Framer's ramp applies NO tracking at any size, so ANY negative
 * value — however well tuned — reads as a foreign type system next to the rest
 * of aldelo.com. Tracking was the second-loudest discontinuity after leading.
 */
const HERO_HEADLINE_LETTER_SPACING = "normal";

/**
 * Operator item (l), 2026-08-16: vertical rhythm between the hero's three
 * copy elements — headline → subheadline → CTA. Was a flat 10px then 20px at
 * every viewport, which reads as squeezed under the large desktop headline.
 *
 * Expressed as `t.spacing` KEY NAMES rather than pixel numbers so the rhythm
 * rides the shared spacing scale (no new magic numbers), and as a
 * desktop/mobile pair because the band's own padding already differs by
 * breakpoint (44px vs 28px) — growing both gaps uniformly would crowd the
 * mobile band, where the headline is 32px rather than 50px and has far less
 * optical weight to separate from. Desktop steps lg→xl (24→32), mobile
 * md→lg (16→24): in both cases the CTA sits one step further from the
 * paragraph than the paragraph sits from the headline, so the copy reads as
 * two groups (title+caption, then action) rather than three evenly-spaced
 * strangers.
 *
 * KNOWN, DEFERRED (2026-08-16 aldelo.com type continuity): these gaps were
 * tuned against 1.1 headline leading. At 1.5 the headline's line box carries
 * its own half-leading below the last line, so the OPTICAL gap to the caption
 * is now larger than the nominal 24px/16px here. That was left alone
 * deliberately rather than overlooked — the operator scoped this change to
 * title + caption type and asked to judge the rhythm after seeing it live.
 * If it reads too airy, retune HERE (the key names), not by clawing back the
 * leading, which is the value doing the continuity work.
 *
 * Exported through HERO_STYLE_CONTRACT so the co-located test derives BOTH
 * the token and the breakpoint mapping from here (§55) — a retune moves the
 * rendered band and the test together.
 */
const HERO_COPY_RHYTHM = {
  /** Gap below the headline, above the subheadline. */
  headlineToSub: { desktop: "lg", mobile: "md" },
  /** Gap below the subheadline (or the headline, if there is none), above the CTA. */
  subToCta: { desktop: "xl", mobile: "lg" },
} as const;

/**
 * Operator item (q), 2026-08-16: "marketplace Hero's 'Browse Products' button
 * appears ONLY if not on the marketplace main ui" — the ONE view the hero CTA
 * is suppressed on.
 *
 * WHY this view: the shell always wires the CTA to `onNavigateCatalog` (see
 * the StorefrontHero call below), so on the catalog itself the button is a
 * no-op that would scroll the visitor to the grid they are already reading.
 * On every OTHER view the hero renders over — product, cart, checkout,
 * cart-review, order-confirmation, order-detail, account — it is a real way
 * back to browsing, so it stays.
 *
 * Typed as `Route["page"]` so it cannot name a page the router does not have,
 * and compared against the `currentPage` prop the router feeds from the SAME
 * `route` state its `renderRoute()` switch reads — there is deliberately no
 * second, independently-derived notion of "which view am I on" that could
 * drift out of sync with what is actually rendered (§51 family).
 *
 * Exported so the co-located test drives the hide/show boundary from this
 * constant rather than a hardcoded "catalog" duplicate (§55).
 */
export const HERO_CTA_HIDDEN_ON_PAGE: Route["page"] = "catalog";

/**
 * Pages on which the toolbar's product search and sort controls are not rendered. They filter
 * the product catalog, so on the account pages (the account center, and an order opened from
 * it) they only invite a search that silently does nothing there (redesign Task 4, D2). The
 * cart and account buttons stay. Typed as `Route["page"]` for the same reason as
 * HERO_CTA_HIDDEN_ON_PAGE; exported so the co-located test drives the boundary from this set
 * rather than a hardcoded duplicate (rule 55).
 */
export const CATALOG_CONTROLS_HIDDEN_ON_PAGE: ReadonlySet<Route["page"]> = new Set<Route["page"]>([
  "account",
  "order-detail",
]);

// H4: a single subtle entrance (fade+rise, ≤300ms), never looping. Inline
// styles cannot define @keyframes, so inject one idempotent <style> — same
// pattern as EmbedSpinner.ensureKeyframes (embed-shared/primitives).
const HERO_STYLE_ID = "aldelo-hero-keyframes";
const HERO_ENTER_ANIMATION = "aldelo-hero-enter 280ms ease-out backwards";

/**
 * Injects the hero's entrance keyframes once per document.
 *
 * Named ensureHeroKeyframes while it ALSO emitted item (m)'s per-tile float
 * keyframes; the 2026-08-16 redesign removed the montage those floats
 * animated, so this once again covers only the H4 entrance and the name says
 * so again (§48). HERO_ENTER_ANIMATION on the <section> is inert without it —
 * inline styles cannot declare @keyframes.
 */
function ensureHeroEnterKeyframes(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(HERO_STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = HERO_STYLE_ID;
  el.textContent =
    "@keyframes aldelo-hero-enter{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}";
  document.head.appendChild(el);
}

/**
 * The hero's art-direction values that the co-located style-contract test
 * pins. Exported so that test asserts against the CONSTANTS rather than
 * hardcoded duplicates of their current values (§55) — a retune of the art
 * direction should move the rendered band and the test together, and should
 * never be a find-and-replace across two files.
 *
 * Declared AFTER the hero constants it aggregates: `const` bindings sit in the
 * temporal dead zone until their declaration is evaluated, so an object
 * literal placed above them would throw at module-eval time, not merely read
 * stale. Keep new entries below their sources.
 */
export const HERO_STYLE_CONTRACT = {
  bgImageUrl: HERO_BG_IMAGE_URL,
  ink: HERO_INK,
  textSoft: HERO_TEXT_SOFT,
  fallbackBackground: HERO_BACKGROUND,
  edgeBlend: HERO_EDGE_BLEND,
  /** Contrast scrim — layered ONLY above an operator-uploaded backdrop. */
  uploadScrim: HERO_UPLOAD_SCRIM,
  /** That scrim's white alpha. An a11y control — see its note above. */
  uploadScrimAlpha: HERO_UPLOAD_SCRIM_ALPHA,
  /** Max measure of the centred copy column. */
  copyMaxWidth: HERO_COPY_MAX_WIDTH,
  /** Caption measure nested inside that column. */
  subheadlineMaxWidth: HERO_SUBHEADLINE_MAX_WIDTH,
  /** (l) headline → subheadline → CTA gaps, as t.spacing key names per breakpoint. */
  copyRhythm: HERO_COPY_RHYTHM,
  /** Caption size — the aldelo.com body ramp. */
  subheadlineSize: HERO_SUBHEADLINE_SIZE,
  /** Caption leading — the aldelo.com body ramp; honours (l)'s intent. */
  subheadlineLineHeight: HERO_SUBHEADLINE_LINE_HEIGHT,
  /** Headline size per breakpoint — the aldelo.com page-hero h1. */
  headlineSizeDesktop: HERO_HEADLINE_SIZE_DESKTOP,
  headlineSizeMobile: HERO_HEADLINE_SIZE_MOBILE,
  /** Headline leading — the aldelo.com ramp's 1.5, no longer display-tuned 1.1. */
  headlineLineHeight: HERO_HEADLINE_LINE_HEIGHT,
  /** Headline weight — 700, matching aldelo.com; supersedes (o)'s 500. */
  headlineWeight: HERO_HEADLINE_WEIGHT,
  /** Headline tracking — `normal`, matching aldelo.com; supersedes (o)'s -0.01em. */
  headlineLetterSpacing: HERO_HEADLINE_LETTER_SPACING,
  /** H4 entrance shorthand — absent entirely under prefers-reduced-motion. */
  enterAnimation: HERO_ENTER_ANIMATION,
  /** The injected <style> element's id, for the entrance-keyframe test. */
  styleId: HERO_STYLE_ID,
} as const;

/**
 * Whether the visitor prefers reduced motion (H6 — disables the hero's
 * entrance animation; no transform/opacity animation when set). Mirrors
 * useResolvedTheme's matchMedia subscription pattern (embed-shared/theme.tsx)
 * but for a different media feature; kept local since it has exactly one
 * caller. Assumes NO preference (motion OK) when matchMedia is unavailable
 * (old browsers / SSR / jsdom default) — same fail-open precedent as
 * theme.tsx's fallback-to-light.
 */
function usePrefersReducedMotion(): boolean {
  const read = (): boolean => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  };
  const [reduced, setReduced] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      setReduced(false);
      return;
    }
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mql.matches);
    const onChange = (e: { matches: boolean }) => setReduced(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

// Same window-width pattern as ProductGrid's module-private
// useContainerWidth — inline styles cannot carry media queries, so
// responsive switches re-render on resize. Local so the shell stays
// independently testable/movable. NOTE (final whole-branch review, I-5):
// ProductGrid's useContainerWidth returns VIEWPORT width (window.innerWidth)
// and is NOT the shared embed-shared/use-container-width.ts hook of the same
// name, which measures the CONTAINER via ResizeObserver — that shared hook,
// not this pattern, is the one to use for layout that should track its own
// container.
function useWindowWidth(): number {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export interface StorefrontShellProps {
  children: ReactNode;
  onNavigateCatalog: () => void;
  onNavigateCart: () => void;
  onNavigateAccount: () => void;
  onSignIn: () => void;
  /**
   * Current committed search value (post-debounce). The shell seeds its
   * local input from this on mount AND re-syncs whenever it changes
   * externally (e.g. the active-filter chips' clear affordance) — it is not
   * merely an initial value.
   */
  search: string;
  /** Called with the debounced search value after SEARCH_DEBOUNCE_MS of no typing. */
  onSearchChange: (value: string) => void;
  /** Selected category slug, or null for "All categories". */
  category: string | null;
  /** Called with the selected category's slug, or null when "All categories" is chosen. */
  onCategoryChange: (slug: string | null) => void;
  /** Active sort (backend allowlist — CatalogSort). Rendered by the toolbar dropdown. */
  sort: CatalogSort;
  /** Called with the newly selected sort when the toolbar dropdown changes. */
  onSortChange: (sort: CatalogSort) => void;
  /** Active product_type facet filter, or null (raw wire value, e.g. "hardware"). */
  productType: string | null;
  /** Called with the selected product type value, or null when cleared. */
  onProductTypeChange: (value: string | null) => void;
  /**
   * Last-loaded catalog page facets (lifted by the router via CatalogView's
   * onCatalogLoaded). Powers the hero's From-$ chip and resolves the
   * category chip's display name; null until the first page resolves.
   */
  facets: CatalogFacets | null;
  /**
   * Item (q): the route the shell is currently wrapping — the router's OWN
   * `route.page`, passed straight through so this component reads the exact
   * discriminant `renderRoute()` switches on. Today its only consumer is the
   * hero CTA's visibility (see HERO_CTA_HIDDEN_ON_PAGE); it is typed as the
   * whole union rather than a `boolean` precisely so a future view-sensitive
   * affordance can reuse it without the caller inventing a second flag.
   */
  currentPage: Route["page"];
}

// Local D10 hero. Deliberately NOT the shared StoreHeroBand (StoreBands.tsx):
// the redesign hero has backdrop/copy anatomy the plain band lacks, and the
// portal-side <StorePreview> keeps rendering the simple band until it is
// redesigned in its own task. Fail-open contract preserved: no
// hero_headline → renders null.
function StorefrontHero({
  headline,
  subheadline,
  ctaText,
  onCtaClick,
  heroImageUrl,
}: {
  headline: string;
  subheadline?: string;
  ctaText?: string;
  /**
   * In-app navigation for the CTA. The shell ALWAYS supplies this
   * (`onCtaClick={onNavigateCatalog}`, and `onNavigateCatalog` is a required
   * prop of StorefrontShellProps), so the CTA is always a <button>.
   *
   * There is deliberately NO `ctaUrl` here. It existed until 2026-08-16 as an
   * `<a href>` fallback that was provably unreachable in this component, and
   * carrying it implied a link mode this hero never has. The operator-editable
   * `hero_cta_url` field is NOT dead and was NOT removed — the SHARED
   * `StoreHeroBand` (StoreBands.tsx) still renders it as an anchor for the
   * operator portal preview (StorePreview.tsx), which passes `ctaUrl` and no
   * `onCtaClick`. Do not "tidy" that one away by symmetry with this one.
   */
  onCtaClick?: () => void;
  /**
   * Operator-uploaded hero image URL. Once verified loadable it REPLACES
   * HERO_BG_IMAGE_URL as the band's backdrop and brings HERO_UPLOAD_SCRIM
   * with it; until then (and permanently, if it never loads) the curated
   * photo keeps painting. Was a floating image card until the 2026-08-16
   * redesign removed every floating element from the band (§48).
   *
   * This does NOT affect whether the hero renders at all — `hero_headline`
   * remains the sole switch for that (H3), enforced by the caller
   * (StorefrontShell), not here.
   */
  heroImageUrl?: string;
}) {
  const t = useEmbedTokens();
  const width = useWindowWidth();
  const isDesktop = width >= HERO_DESKTOP_MIN_WIDTH;
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    ensureHeroEnterKeyframes();
  }, []);

  // Item (l): the copy column's vertical rhythm, resolved for this breakpoint.
  // Both gaps come off the shared spacing scale via HERO_COPY_RHYTHM so the
  // desktop/mobile mapping has exactly one home (and the test reads it from
  // there rather than re-deciding it — §55).
  const rhythm = isDesktop ? "desktop" : "mobile";
  const gapHeadlineToSub = t.spacing[HERO_COPY_RHYTHM.headlineToSub[rhythm]];
  const gapSubToCta = t.spacing[HERO_COPY_RHYTHM.subToCta[rhythm]];

  // PRELOAD-BEFORE-SWAP. The upload is now a CSS background-image, and a CSS
  // background fires NO error event — so the `onError` fallback the old image
  // CARD relied on could not survive the move to a backdrop. Dropping the URL
  // straight into the background stack would leave a 404'd or otherwise broken
  // upload painting nothing at all, silently, with no way to notice.
  //
  // So: probe the URL with an off-DOM Image() and promote it into the stack
  // only once it has actually decoded. Until then — and forever, if it fails —
  // the curated HERO_BG_IMAGE_URL keeps painting, preserving the same
  // fail-open contract the card had, re-expressed for a layer that cannot
  // report its own failure. The probe doubles as a cache warm, so the swap
  // paints immediately instead of flashing an empty band.
  const [uploadedBackdrop, setUploadedBackdrop] = useState<string | null>(null);
  useEffect(() => {
    // Clear any previously-verified image FIRST: when the operator configures
    // a NEW url, the band must fall back to the curated photo while the new
    // one is probed, never keep showing the old upload under a new config.
    setUploadedBackdrop(null);
    if (!heroImageUrl) return;
    // Same fail-open precedent as usePrefersReducedMotion's matchMedia guard —
    // no Image constructor (SSR) simply means the curated photo stands.
    if (typeof window === "undefined" || typeof window.Image !== "function") return;

    let cancelled = false;
    const probe = new window.Image();
    probe.onload = () => {
      if (!cancelled) setUploadedBackdrop(heroImageUrl);
    };
    // Deliberately empty: a failed probe leaves the state null, and null IS
    // the fallback. Assigned rather than omitted so a broken upload resolves
    // quietly instead of surfacing as an unhandled image error.
    probe.onerror = () => {};
    probe.src = heroImageUrl;
    return () => {
      // A late callback must neither resurrect a URL the config has moved off
      // nor setState after unmount; detaching both handlers also lets the
      // probe be collected while an in-flight request drains.
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [heroImageUrl]);

  // The backdrop actually painted, plus the scrim that guards it. On the
  // default (no verified upload) path the scrim string is EMPTY, so the layer
  // stack below is byte-for-byte what (k2) shipped.
  const backdropUrl = uploadedBackdrop ?? HERO_BG_IMAGE_URL;
  const backdropScrim = uploadedBackdrop ? `${HERO_UPLOAD_SCRIM}, ` : "";

  const ctaStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    textDecoration: "none",
    fontFamily: "inherit",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    height: t.buttonHeight,
    padding: "0 22px",
    borderRadius: t.borderRadiusControl,
    border: "none",
    // (k2) INVERTED from the mock's white-button/navy-ink pairing: on the
    // light band a white button has no edge against a near-white photo (and
    // is swallowed outright by the white edge blend if the copy column is
    // narrow). A solid ink button is the smallest change that keeps the CTA
    // unmistakably a button — it is also the only dark mass in the
    // composition, so it reads as the primary action. Fixed hero art
    // direction: t.colors.white is #FFFFFF in both themes, so the label
    // stays 17.7:1 against HERO_INK.
    background: HERO_INK,
    color: t.colors.white,
  };

  return (
    <section
      data-testid="store-hero-band"
      aria-label="Marketplace introduction"
      style={{
        // 2026-08-16 redesign: ONE centred column. Was a two-column grid
        // (copy | floating cards) until the operator asked for the product
        // tiles to go ("too busy") — with a single child there is no explicit
        // column track left to declare, and justifyItems/textAlign centre the
        // copy at every breakpoint rather than only below the old mobile fork.
        display: "grid",
        justifyItems: "center",
        alignItems: "center",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        // (k2): four white edge blends → the backdrop photo → light gradient
        // fallback. CSS paints background layers first-listed-on-top, so the
        // edge blends precede the photo and the fallback comes last.
        //
        // 2026-08-16: the backdrop is the operator's UPLOAD once it has been
        // verified loadable, otherwise the curated (k2) page-hero photo. The
        // contrast scrim rides directly above the layer it guards and is
        // present ONLY on the upload path — with no upload `backdropScrim` is
        // "" and this resolves to exactly the (k2) stack.
        backgroundImage: `${HERO_EDGE_BLEND}, ${backdropScrim}url("${backdropUrl}"), ${HERO_BACKGROUND}`,
        // A single value applies to every layer. Gradients have no intrinsic
        // size, so `cover` resolves to 100% 100% for the four edge blends and
        // the fallback, and crops the photo as before.
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: HERO_INK,
        borderRadius: t.borderRadiusLg,
        padding: isDesktop ? "44px" : "28px",
        margin: 0,
        marginBottom: t.spacing.lg,
        minHeight: isDesktop ? HERO_MIN_HEIGHT_DESKTOP : undefined,
        // H4: single subtle entrance (fade+rise, ≤300ms), never looping.
        // H6: prefers-reduced-motion disables it entirely — no
        // transform/opacity animation runs, the section renders at its
        // final (opacity:1, no transform) state from the start.
        animation: reducedMotion ? undefined : HERO_ENTER_ANIMATION,
      }}
    >
      {/* The centred copy column. Capped so the headline cannot span a wide
          host page edge-to-edge; the section's justifyItems/textAlign do the
          centring, so this owns measure only. */}
      <div style={{ maxWidth: HERO_COPY_MAX_WIDTH }}>
        <h1
          style={{
            margin: 0,
            fontSize: isDesktop ? HERO_HEADLINE_SIZE_DESKTOP : HERO_HEADLINE_SIZE_MOBILE,
            // 2026-08-16 aldelo.com type continuity: 700 weight, `normal`
            // tracking, 1.5 leading — the measured host-site page-hero h1.
            // Supersedes item (o)'s 500/-0.01em; see HERO_HEADLINE_WEIGHT's
            // note for the approved reversal and why it is contrast-neutral.
            fontWeight: HERO_HEADLINE_WEIGHT,
            letterSpacing: HERO_HEADLINE_LETTER_SPACING,
            lineHeight: HERO_HEADLINE_LINE_HEIGHT,
          }}
        >
          {headline}
        </h1>
        {subheadline ? (
          <p
            style={{
              // Item (l): was a flat 10px. Margin-top only — the paragraph
              // owns the gap ABOVE it, the CTA wrapper owns the gap above
              // itself, so no margin ever collapses between them and the
              // rhythm survives the subheadline being absent.
              // 2026-08-16: `auto` sides centre the capped paragraph inside
              // the wider copy column (the column is sized for the 50px
              // headline, this measure for readability).
              margin: `${gapHeadlineToSub}px auto 0`,
              color: HERO_TEXT_SOFT,
              fontSize: HERO_SUBHEADLINE_SIZE,
              lineHeight: HERO_SUBHEADLINE_LINE_HEIGHT,
              maxWidth: HERO_SUBHEADLINE_MAX_WIDTH,
            }}
          >
            {subheadline}
          </p>
        ) : null}
        {/* Operator directive 2026-08-16: the "From $X" chip is REMOVED (D10's
            min-price pill retired) — not needed. The CTA below follows the
            subheadline directly. */}
        {ctaText && onCtaClick ? (
          <div style={{ marginTop: gapSubToCta }}>
            <button type="button" onClick={onCtaClick} style={ctaStyle}>
              {ctaText}
            </button>
          </div>
        ) : null}
      </div>
      {/* Operator directive 2026-08-16: NOTHING follows the copy column. Both
          floating-card arms are gone — the three-product montage AND the
          single uploaded-image card ("too busy") — so the band is one centred
          column over the backdrop, and the upload (when there is one) IS that
          backdrop rather than a card on top of it. */}
    </section>
  );
}

export function StorefrontShell({
  children,
  onNavigateCatalog,
  onNavigateCart,
  onNavigateAccount,
  onSignIn,
  search,
  onSearchChange,
  category,
  onCategoryChange,
  sort,
  onSortChange,
  productType,
  onProductTypeChange,
  facets,
  currentPage,
}: StorefrontShellProps) {
  const t = useEmbedTokens();
  const { token } = useAuth();
  const { itemCount } = useCart();
  // Operator-edited StoreContent marketing fields (Task 4). Drives the hero,
  // footer band, and document head SEO below. When none are configured (the
  // default until an operator sets them) every field is undefined and the
  // hero/footer render null — the toolbar-only layout is unchanged.
  const config = useStorefrontConfig();

  // Best-effort, idempotent SEO: when the operator configured a title/description
  // in StoreContent, reflect it into the document head. The embed is hosted
  // inside a customer's Framer page, so we cannot assume control of the page's
  // initial <title>/<meta> — we only SET when the operator provided a value,
  // never blank an existing one (fail-open). The typeof-document guard is
  // defensive (the embed is client-rendered) and harmless.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (config.seo_title) document.title = config.seo_title;
  }, [config.seo_title]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!config.seo_description) return;
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", config.seo_description);
  }, [config.seo_description]);

  // Local echo of the search input so keystrokes feel instant; the debounced
  // value is reported to the parent (and from there down to CatalogView) via
  // onSearchChange after SEARCH_DEBOUNCE_MS of no further typing.
  const [searchInput, setSearchInput] = useState(search);
  const isFirstSearchEffect = useRef(true);
  // The last value this component fired up via onSearchChange (TD-314). The
  // sync effect below uses it to distinguish an EXTERNAL `search` change
  // (e.g. clear-filters — re-seed the input) from the component's OWN debounce
  // round-trip (do NOT re-seed). Without this, createRoot can interleave the
  // debounce firing and the sync effect such that keystrokes typed in the
  // window between them are clobbered by setSearchInput(search), dropping a
  // trailing character.
  //
  // PRECONDITION (load-bearing): the skip-reseed branch below only stays
  // correct while the parent wires `onSearchChange` to the RAW `setSearch`
  // (MarketplaceEmbed.tsx passes `onSearchChange={setSearch}`). That guarantees
  // the `search` prop lands as EXACTLY the value this component fired, so
  // `search === lastFiredSearchRef.current` holds and the sync effect
  // short-circuits. If a future refactor wraps setSearch — e.g. trims,
  // guards, or transforms the value before storing it — `search` will diverge
  // from lastFiredSearchRef, the sync effect will re-seed searchInput, and the
  // TD-314 trailing-character clobber silently returns. Do not wrap setSearch
  // without re-deriving this invariant. NOTE: the Task-11 active-filter chips
  // call the same onSearchChange("") channel and update the ref to match, so
  // the invariant holds for that path too.
  const lastFiredSearchRef = useRef(search);

  // Re-sync local input state ONLY for external changes (e.g. a parent reset).
  // When `search` equals what this component just fired, re-seeding would
  // clobber keystrokes typed in the interleave window (TD-314).
  useEffect(() => {
    if (search !== lastFiredSearchRef.current) {
      setSearchInput(search);
      lastFiredSearchRef.current = search;
    }
  }, [search]);

  useEffect(() => {
    // Skip the mount run — firing onSearchChange with the initial value
    // (typically "") would be a spurious no-op call and an unnecessary
    // catalog refetch.
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return;
    }
    const timer = setTimeout(() => {
      // Record the fired value BEFORE reporting it so the sync effect above can
      // recognize the resulting parent `search` change as our own and skip the
      // re-seed (TD-314).
      lastFiredSearchRef.current = searchInput;
      onSearchChange(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // onSearchChange is a caller-supplied callback, not a dependency we want
    // to re-run the debounce timer for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Task 4a: sized to the shared buttonHeight token (the canonical pattern
  // already used by the hero CTA at ctaStyle above) so Sign in/Account and
  // Cart match the 44px search input / sort select in the same toolbar row —
  // content-driven xs/md padding (the old catalog-only toolbar's sizing
  // strategy, reused verbatim pre-Task-6) collapsed both to 27px live,
  // visibly shorter than the fields beside them.
  // Sign in / Account: primary fill, bold onAction text.
  // Corner rule: every storefront button uses the design system's control
  // radius through ONE shared token (t.borderRadiusControl, the same value
  // EmbedButton, the hero and store-band buttons, tabs and catalog load-more
  // use) — never the pill shape and never a literal. Fields keep the
  // design-system field radius (t.borderRadius), so a button beside a field
  // is deliberately a different corner. Do not restore the oval thinking the
  // flatter corner is an accident. Fill/weight/height unchanged;
  // height still the shared buttonHeight token so it matches the
  // search/sort fields (Task 4a).
  const authBtn: CSSProperties = {
    height: t.buttonHeight,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: t.colors.primary,
    // onAction, not white: the design-system dark on-action is near-black
    // (white on the dark action colour is about 3:1).
    color: t.colors.onAction,
    border: "none",
    borderRadius: t.borderRadiusControl,
    padding: `0 ${t.spacing.lg}px`,
    fontSize: t.fontSize.sm,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: t.fontFamily,
  };

  // Operator item (j), 2026-08-16: Cart was a LIGHT OVAL ICON button — a cart
  // glyph in a bordered cardBg pill, LEFT of the auth button. The item count
  // badge (rendered below, still a 999px pill — NOT covered by this ruling)
  // is absolutely positioned against this button — the position:relative
  // below is now LOAD-BEARING (it anchored nothing from Task 4a until today).
  // Operator item (n), 2026-08-16: widened. Width is now height + a fixed
  // boost (see CART_PILL_EXTRA_WIDTH) rather than == height; the HEIGHT is
  // untouched, so the Task 4a row-alignment contract still holds.
  // Corner rule: the button's own corner is the shared control-radius token
  // (t.borderRadiusControl, same as EmbedButton and authBtn above), not an
  // oval — do not restore the oval thinking the flatter corner is an
  // accident. The count badge above stays a pill; only the button shape
  // is the control radius.
  const cartIconBtn: CSSProperties = {
    height: t.buttonHeight,
    width: t.buttonHeight + CART_PILL_EXTRA_WIDTH,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: t.colors.cardBg,
    border: `1px solid ${t.colors.border}`,
    borderRadius: t.borderRadiusControl,
    padding: 0,
    cursor: "pointer",
    fontFamily: t.fontFamily,
    color: t.colors.text,
    position: "relative",
  };

  // Active-filter chip (one per active filter; ✕ clears it via the router's
  // callback). raw <button>: pill chip styling is bespoke to this row —
  // EmbedButton/EmbedBadge force their own chrome. type="button" at birth (§73).
  const filterChipStyle: CSSProperties = {
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: `1px solid ${t.colors.primary}`,
    background: t.colors.primaryTint,
    color: t.colors.link,
    padding: "5px 12px",
    borderRadius: 999,
    whiteSpace: "nowrap",
  };

  const clearSearchFilter = () => {
    // Fire the clear through the same channel as typing (onSearchChange) and
    // record it as our own fired value so the TD-314 sync effect treats the
    // parent's resulting search="" as OUR round-trip (no re-seed needed — the
    // local input is already cleared here for immediate feedback).
    setSearchInput("");
    lastFiredSearchRef.current = "";
    onSearchChange("");
  };

  // Resolve the active category chip's display NAME from the facets (the
  // router holds only the slug). Falls back to the raw slug until/unless the
  // facets can resolve it (e.g. a filtered result set that dropped the facet).
  const categoryChipLabel =
    facets?.categories.find((c) => c.slug === category)?.name ?? category ?? "";

  // Item (q): suppress the hero CTA on the catalog, where it would only send
  // the visitor to what they are already looking at (see
  // HERO_CTA_HIDDEN_ON_PAGE for the full rationale). Expressed as "there is no
  // CTA text on this view" rather than a second `showCta` prop: the hero
  // already has one well-tested no-CTA branch (it also drops the gap wrapper
  // above the button, so the copy column ends cleanly on the subheadline
  // instead of leaving a dangling margin), and a second switch for the same
  // node would be two ways to express one state.
  const heroCtaText = currentPage === HERO_CTA_HIDDEN_ON_PAGE ? undefined : config.hero_cta_text;

  return (
    <div>
      {config.hero_headline ? (
        <StorefrontHero
          headline={config.hero_headline}
          subheadline={config.hero_subheadline}
          ctaText={heroCtaText}
          onCtaClick={onNavigateCatalog}
          heroImageUrl={config.hero_image_url}
        />
      ) : null}
      <div
        data-testid="shell-toolbar"
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: t.spacing.sm,
          marginBottom: t.spacing.lg,
        }}
      >
        {/* Operator directive 2026-08-16: the redundant "Marketplace" word is
            GONE from the toolbar — the hero above already names the store, so
            the heading was noise. With it removed, the space-between row puts
            the search + sort controls FIRST (left-aligned) and the
            Account/Cart buttons keep the right edge. Catalog navigation
            remains via the hero CTA and the browser back stack. */}
        {!CATALOG_CONTROLS_HIDDEN_ON_PAGE.has(currentPage) && (
          <div style={{ display: "flex", gap: t.spacing.sm, flex: `1 1 ${CONTROLS_FLEX_BASIS}px`, maxWidth: CONTROLS_MAX_WIDTH }}>
            <div style={{ flex: 1 }}>
              <EmbedInput
                type="search"
                aria-label="Search products"
                placeholder="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div style={{ flex: "0 0 auto", minWidth: SORT_MIN_WIDTH }}>
              <EmbedSelect
                aria-label="Sort products"
                value={sort}
                onChange={(e) => {
                  const next = e.target.value as CatalogSort;
                  // Only values from SORT_OPTIONS can be selected, so the cast
                  // cannot smuggle an invalid sort onto the wire (backend 400s
                  // otherwise — the dropdown is the single source of sort).
                  onSortChange(next);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </EmbedSelect>
            </div>
          </div>
        )}
        {/* Operator item (j), 2026-08-16: the right-edge pair is [Cart icon
            button][Sign in / Account button] — Cart moved LEFT of the auth
            button. raw <button>s: custom toolbar chrome (primitive sweep
            TD-NEW); EmbedButton's fixed height/weight would fight both
            treatments. Both corners are the shared control-radius token
            (t.borderRadiusControl) — see authBtn/cartIconBtn above. */}
        {/* With the search and sort controls hidden, space-between would leave the pair alone
            on the left; marginLeft:auto keeps it on the right edge. It is applied ONLY in that
            case: on the catalog a wrapped toolbar line has always left-aligned the pair. */}
        <div style={{ display: "flex", gap: t.spacing.sm, marginLeft: CATALOG_CONTROLS_HIDDEN_ON_PAGE.has(currentPage) ? "auto" : undefined }}>
          <button
            type="button"
            onClick={onNavigateCart}
            style={cartIconBtn}
            aria-label={itemCount > 0 ? `Cart, ${itemCount} items` : "Cart"}
          >
            {/* Feather-style shopping-cart glyph, stroke=currentColor. The
                icon is decorative — the aria-label above carries the meaning
                (including the count) for AT. */}
            <svg
              aria-hidden="true"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="9" cy="21" r="1" />
              <circle cx="20" cy="21" r="1" />
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
            </svg>
            {itemCount > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: 18,
                  height: 18,
                  padding: "0 4px",
                  borderRadius: 999,
                  backgroundColor: t.colors.primary,
                  color: t.colors.onAction,
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: "18px",
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {itemCount}
              </span>
            )}
          </button>
          {token ? (
            <button type="button" onClick={onNavigateAccount} style={authBtn}>
              Account
            </button>
          ) : (
            <button type="button" onClick={onSignIn} style={authBtn}>
              Sign in
            </button>
          )}
        </div>
      </div>
      {/* Active-filter chips (Task 11): one ✕ chip per active filter so the
          current filter state is always visible and individually clearable,
          mirroring the rail's single-select semantics. */}
      {(search || category || productType) && (
        <div
          data-testid="active-filter-chips"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: t.spacing.sm,
            marginBottom: t.spacing.md,
          }}
        >
          {category && (
            <button
              type="button"
              aria-label={`Remove category filter: ${categoryChipLabel}`}
              style={filterChipStyle}
              onClick={() => onCategoryChange(null)}
            >
              <span>{categoryChipLabel}</span>
              <span aria-hidden="true">✕</span>
            </button>
          )}
          {productType && (
            <button
              type="button"
              aria-label={`Remove product type filter: ${typeLabel(productType)}`}
              style={filterChipStyle}
              onClick={() => onProductTypeChange(null)}
            >
              <span>{typeLabel(productType)}</span>
              <span aria-hidden="true">✕</span>
            </button>
          )}
          {search && (
            <button
              type="button"
              aria-label={`Remove search filter: ${search}`}
              style={filterChipStyle}
              onClick={clearSearchFilter}
            >
              <span>“{search}”</span>
              <span aria-hidden="true">✕</span>
            </button>
          )}
        </div>
      )}
      {/* The marketplace is public (TD-649): the route content slot always
          renders. The toolbar above and the footer band below are unchanged. */}
      {children}
      <StoreFooterBand
        footerText={config.footer_text}
        aboutText={config.about_text}
        contactEmail={config.contact_email}
        contactPhone={config.contact_phone}
      />
    </div>
  );
}
